/**
 * /content-roadmap — SEO page for "what is a content roadmap" (the single
 * biggest keyword in the set, ~29k current traffic, KD ~0–5). FIT CAVEAT:
 * the searcher is usually a CONTENT MARKETER, not a PM/founder — intent is
 * adjacent, not core. So this page is genuinely useful for a content
 * marketer first; the Draftlize turn is gentle and honest — any roadmap
 * (content or product) drifts when the decisions under it move, and
 * Draftlize is the decision layer for whoever plans it. We do NOT pretend
 * to be a content-marketing tool.
 *
 * English-only + inline. Chinese variant in ContentRoadmapZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function ContentRoadmap() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/content-roadmap"
      documentTitle="What Is a Content Roadmap? Template + Examples · Draftlize"
      documentDescription="A content roadmap maps what you'll publish, when, and why — tied to goals and audience. What it is, how to build one, and how to keep it from drifting."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Content roadmaps</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 62px)', lineHeight: 1.03, margin: '12px 0 24px' }}>
            What is a content roadmap?<br/>
            <em>And how to keep it honest.</em>
          </h1>
          <p className="lede">
            A <em>content roadmap</em> is a plan of what you&apos;ll publish, in what order, and — the part that separates a roadmap from a calendar — <em>why</em>: which audience, which goal, which stage of the funnel each piece serves. It&apos;s how a content team aligns on direction instead of churning out posts and hoping. This page tells you plainly what a content roadmap is, how it differs from a calendar, how to build one, and then the honest part: a roadmap is only as good as the strategy underneath it, and when that strategy shifts, the roadmap quietly stops matching.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">So, <em>what is a content roadmap?</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            A content roadmap is the bridge between your content strategy (&quot;we win organic search for AI-native PMs&quot;) and your day-to-day calendar (&quot;publish the PRD guide Tuesday&quot;). It groups planned content into themes or pillars tied to a goal and an audience, sequenced over time. The point isn&apos;t the list of titles — it&apos;s that every piece can answer &quot;who is this for and what is it supposed to do,&quot; so you stop publishing to fill a slot.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            It is not a content <em>calendar</em> (that&apos;s the dated schedule of what goes out when) and not an editorial plan for a single piece. The roadmap sits above both: it&apos;s strategy made concrete enough to prioritize against, loose enough that next quarter&apos;s themes can flex as you learn what resonates.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Don&apos;t confuse them</span>
            <h2 className="serif">Roadmap vs calendar <em>vs editorial plan.</em></h2>
          </div>
          <p>Three layers that get called &quot;the content plan.&quot; Keeping them separate is what stops your calendar from becoming a treadmill with no direction.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>Artifact</th>
              <th>Answers</th>
              <th>Horizon</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Content roadmap</td><td className="yes">What themes &amp; why, by audience/goal</td><td>Quarters, loose</td></tr>
            <tr><td>Content calendar</td><td className="partial">What publishes on which date</td><td>Weeks, firm</td></tr>
            <tr><td>Editorial plan</td><td className="partial">How one piece gets made</td><td>Per piece</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">How to build one <em>in five steps.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Start from the goal and audience</h3>
            <p>Name what the content is for — organic growth, product activation, thought leadership — and exactly who you&apos;re writing for. Everything downstream is a bet in service of this. Skip it and you get a calendar of topics with no through-line.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Define themes / pillars</h3>
            <p>Group content into a few durable themes (&quot;PRDs&quot;, &quot;decision tracking&quot;, &quot;AI-assisted PM&quot;) rather than a flat list of titles. Themes let specific pieces change without the roadmap changing, and they map cleanly to search clusters.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Prioritize the pieces</h3>
            <p>Score candidate pieces by reach, business impact, and effort so the order reflects value, not whoever pitched loudest. A <a href="/prioritization-frameworks" style={{ color: 'var(--amber)' }}>prioritization framework</a> works for content as well as product.</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">Sequence over a horizon</h3>
            <p>Lay the themes out across quarters — concrete for the current one, directional after. Hand the committed near-term pieces to a content calendar; keep the roadmap at the altitude of themes.</p>
          </div>
          <div className="problem-col">
            <span className="num">V</span>
            <h3 className="serif">Revisit as you learn</h3>
            <p>What resonates changes the bets. The hard part is that when the strategy shifts, the roadmap and every piece justified by the old plan should shift too — and nothing in a spreadsheet tells you which ones. That&apos;s the next section.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">A roadmap is only as live as the <em>decisions under it.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Honest note: Draftlize is not a content-marketing tool — for calendars and publishing, use the tools built for that. But a content roadmap, like a product roadmap, rests on decisions (the audience, the goal, the positioning), and when one of those changes, the roadmap quietly stops matching. That decision layer is what Draftlize holds.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The bets become cards</h3>
            <p>Target audience, the goal, the positioning angle each piece leans on — in Draftlize these are addressable cards, not assumptions buried in a planning doc. A planned piece can point at the exact bet it serves.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A bet changes, dependents flag stale</h3>
            <p>You re-segment the audience, or the positioning shifts. Every roadmap theme and planned piece that leaned on the old bet auto-flags stale — so a strategy change propagates to the plan instead of leaving you publishing to a brief that no longer holds.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your AI agent drafts from the live plan</h3>
            <p>When an agent helps outline the next piece over MCP, it reads the current audience and goal — not last quarter&apos;s. The roadmap stays the source of truth your content is actually built against.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A content roadmap doesn&apos;t fail because the calendar slipped. It fails when the strategy moved and the plan didn&apos;t move with it.
        </blockquote>
        <cite>Keep your calendar and publishing tools. Hold the decisions the roadmap rests on.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is the difference between a content roadmap and a content calendar?', a: 'A content roadmap is strategic — it groups planned content into themes tied to a goal and audience, over loose timeframes. A content calendar is operational — it says exactly what publishes on which date. The roadmap explains why and roughly when; the calendar pins the dates. You need both, in that order.' },
            { q: 'How is a content roadmap different from a product roadmap?', a: 'Same shape, different subject. A product roadmap plans what you\'ll build; a content roadmap plans what you\'ll publish. Both organize work into themes tied to goals, both should avoid faking precise dates, and both quietly drift when the strategy underneath them changes.' },
            { q: 'Who owns the content roadmap?', a: 'Usually the content lead or head of marketing, built with input from SEO, product marketing, and sales on what the audience needs. Ownership means keeping it tied to the goal and current — not personally writing every piece.' },
            { q: 'What tools do I need for a content roadmap?', a: 'For the roadmap and calendar themselves, a dedicated content tool, a spreadsheet, or a project tool like Notion or Asana all work. Draftlize is not one of those — it sits underneath, holding the audience and strategy decisions the roadmap rests on so they don\'t silently go stale.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Hold the decisions under your roadmap <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Plan and publish wherever you already do. Add Draftlize for the layer none of those tools have: keeping the audience, goal, and positioning your content roadmap rests on current — so the plan and the strategy never quietly disagree.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
